import { Alert, Button, Col, Collapse, DatePicker, Form, Input, Row, Space, Switch } from 'antd';
import FormLabel from 'components/FormLabel';
import MultilineInput from 'components/MultilineInput';
import WebsiteLanguageSelect from 'components/WebsiteLanguageSelect';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useFormatMessage from 'hooks/useFormatMessage';

import LanguageTag from 'components/LanguageTag';
import SurveyGroupSelect from 'components/SurveyGroupSelect';
import { DeviceType, SurveyType } from 'shared/services/prisma/types';
import useConfig from 'hooks/useConfig';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import DeviceTypeIcons from 'components/DeviceTypeIcons';
import QRModal from 'components/QRModal';
import queryString from 'query-string';
import { QrcodeOutlined } from '@ant-design/icons';
import { DISTRIBUTION_STATUS } from 'shared/constants/distribution';
import DistributionStatusTag from 'components/DistributionStatusTag';
import InputInfoCircle from 'components/InputInfoCircle';
import messages from './messages';

const PublishRow = ({ name, survey, customerId, websiteId, showLabels = false }) => (
  <Row gutter={16}>
    <Col span={4}>
      <Form.Item label={showLabels && 'Language'}>
        <LanguageTag {...survey} />
      </Form.Item>
    </Col>
    <Col span={20}>
      <Form.Item name={[name, 'publishDetails', survey.id, 'websiteLanguageId']} label={showLabels && <FormLabel label={messages.websiteLanguage} description={messages.websiteLanguageDescription} />}>
        <WebsiteLanguageSelect customerId={customerId} websiteId={websiteId} allowClear />
      </Form.Item>
    </Col>
  </Row>
);

const PublishPeriod = ({ name }) => {
  const $t = useFormatMessage();

  return (
    <Row gutter={16}>
      <Col span={8}>
        <Form.Item name={[name, 'startDate']} label={<FormattedMessage {...messages.startDate} />}>
          <DatePicker allowClear showTime placeholder={$t(messages.never)} />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item name={[name, 'endDate']} label={<FormattedMessage {...messages.endDate} />}>
          <DatePicker allowClear showTime placeholder={$t(messages.never)} />
        </Form.Item>
      </Col>
    </Row>
  );
};

const PublishLink = ({ survey, distribution, onShowQR, params, showDeviceType }) => {
  const { config } = useConfig();
  const url = queryString.stringifyUrl({ url: `${config.survey.baseUrl}d/${distribution.id}/`, query: params });
  return (
    <Row gutter={16} style={{ marginTop: '1em' }}>
      <Col style={{ alignItems: 'center', display: 'flex' }}>
        <DistributionStatusTag {...distribution} />
      </Col>
      <Col style={{ alignItems: 'center', display: 'flex' }}>
        <LanguageTag {...survey} />
      </Col>
      {showDeviceType && (
        <Col style={{ alignItems: 'center', display: 'flex' }}>
          <DeviceTypeIcons deviceTypes={distribution.deviceTypes} />
        </Col>
      )}
      <Col flex="auto">
        <Input value={url} />
      </Col>
      <Col>
        <CopyToClipboardButton textToCopy={url}>Copy link</CopyToClipboardButton>
      </Col>
      <Col>
        <Button icon={<QrcodeOutlined />} onClick={() => onShowQR(url)}>View QR code</Button>
      </Col>
    </Row>
  );
};

const CampaignParameters = ({ onChange }) => (
  <Form onValuesChange={(_, values) => onChange(values)} component={false}>
    <Row gutter={[16, 16]}>
      <Col span={6}>
        <Form.Item name="mtm_campaign" label={<FormLabel label="Name" description="A descriptive name for the campaign, e.g. a blog post title or email campaign name." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_kwd" label={<FormLabel label="Keyword" description="The specific keyword that someone searched for, or category of interest." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_source" label={<FormLabel label="Source" description="The actual source of the traffic, e.g. newsletter, twitter, ebay, etc." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_medium" label={<FormLabel label="Medium" description="The type of marketing channel, e.g. email, social, paid, etc." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_content" label={<FormLabel label="Content" description="This is a specific link or content that somebody clicked. e.g. banner, big-green-button." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_cid" label={<FormLabel label="ID" description="A unique identifier for your specific ad. This parameter is often used with the numeric IDs automatically generated by advertising platforms." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_group" label={<FormLabel label="Group" description="The audience your campaign is targeting e.g. customers, retargeting, etc." />}>
          <Input />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item name="mtm_placement" label={<FormLabel label="Placement" description="The placement on an advertising network e.g. newsfeed, sidebar, home-banner, etc." />}>
          <Input />
        </Form.Item>
      </Col>
    </Row>
  </Form>
);

const PublishOnWebsite = ({ name, survey, translations }) => {
  const $t = useFormatMessage();

  return (
    <>
      <h2>
        Publish survey
        <InputInfoCircle title={<FormattedMessage {...messages.publishWebsiteHelpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
      </h2>

      <Row gutter={16}>
        <Col span={16}>
          <PublishPeriod name={name} />

          <PublishRow name={name} survey={survey} websiteId={survey.website?.id} showLabels />
          {translations.map((s) => (
            <PublishRow key={s.id} name={name} survey={s} customerId={survey.customer.id} websiteId={survey.website?.id} />
          ))}

        </Col>
        <Col span={8}>
          {survey.version != null && <Alert style={{ marginBottom: '1em' }} message="Only one version of the survey can be active. By publishing this version other versions will be deactivated when this starts." type="info" showIcon />}
        </Col>
      </Row>
      {survey.type !== SurveyType.General && (
        <Form.Item name={[name, 'includedUrls']} label={<FormLabel label={messages.includedUrls} description={messages.includedUrlsDescription} />}>
          <MultilineInput placeholder={$t(messages.urlsPlaceholder)} />
        </Form.Item>
      )}
      <Form.Item name={[name, 'excludedUrls']} label={<FormLabel label={messages.excludedUrls} description={messages.excludedUrlsDescription} />}>
        <MultilineInput placeholder={$t(messages.urlsPlaceholder)} />
      </Form.Item>
      <Form.Item name={[name, 'customExpression']} label={<FormLabel label={messages.customExpression} description={messages.customExpressionDescription} extra={<a href="https://academy.extellio.com/knowledge-base/surveys/how-to-write-advanced-triggers-for-your-surveys" target="_blank">Read the docs</a>} />}>
        <Input.TextArea placeholder={$t(messages.customExpressionPlaceholder)} autoSize={{ minRows: 3, maxRows: 10 }} />
      </Form.Item>
      <Form.Item name={[name, 'excludedIPs']} label={<FormattedMessage {...messages.excludedIPs} />}>
        <MultilineInput placeholder={$t(messages.excludedIPsPlaceholder)} />
      </Form.Item>
      {survey.type !== SurveyType.Feedback && (
        <Form.Item name={[name, 'groupSurveyIds']} label={<FormLabel label={messages.sharedSurveys} description={messages.sharedSurveysDescription} />}>
          <SurveyGroupSelect customerId={survey.customer.id} websiteIds={[survey.website?.id]} treeCheckable exclude={[survey.languageParent?.id]} showChildren={false} />
        </Form.Item>
      )}
    </>
  );
};

const PublishCX = ({ name, survey, translations }) => {
  const [showQR, setShowQR] = useState(false);
  const [showDeviceLinks, setShowDeviceLinks] = useState(false);
  const [params, setParams] = useState();

  return (
    <>
      <h2>
        Publish survey
        <InputInfoCircle title={<FormattedMessage {...messages.publishCxHelpText} />} iconStyle={{ marginLeft: 8, width: 16 }} />
      </h2>

      <PublishPeriod name={name} />

      <h2>Distribution links</h2>
      <p>Use these urls to distribute the survey to your recipients. You can also add campaign parameters to track your responses in more detail.</p>
      <p>
        <Space>
          <Switch value={showDeviceLinks} onChange={() => setShowDeviceLinks(!showDeviceLinks)} />
          Device specific urls
        </Space>
      </p>

      <Collapse onChange={() => setParams(null)} destroyInactivePanel items={[{ key: 1, label: 'Campaign parameters', children: <CampaignParameters onChange={setParams} /> }]} />

      {survey.distributions.filter(d => showDeviceLinks || d.deviceTypes.includes(DeviceType.Desktop)).map((d) => (
        <PublishLink key={d.id} name={name} survey={survey} distribution={d} onShowQR={setShowQR} params={params} showDeviceType={showDeviceLinks} />
      ))}

      {translations.map((s) => s.distributions.filter(d => showDeviceLinks || d.deviceTypes.includes(DeviceType.Desktop)).map((d) => (
        <PublishLink key={d.id} name={name} survey={s} distribution={d} onShowQR={setShowQR} params={params} showDeviceLinks={showDeviceLinks} />
      )))}

      {survey.distributions.some(d => d.status !== DISTRIBUTION_STATUS.ACTIVE) && (
        <Alert style={{ marginTop: '1em' }} message="Your survey is not active and will not collect responses, make sure it's active before you send it out to your recipients." type="warning" showIcon />
      )}

      <QRModal open={!!showQR} onCancel={() => setShowQR(false)} value={showQR} />
    </>
  );
};

const PublishStep = ({ name, survey, translations }) => {
  if (survey.type === SurveyType.Cx) {
    return <PublishCX name={name} survey={survey} translations={translations} />;
  }
  return <PublishOnWebsite name={name} survey={survey} translations={translations} />;
};

export default PublishStep;
